import { useEffect, useState } from "react";
import { Pencil, Trash2, Plus, Check, X } from "lucide-react";
import FullScreenModal from "@/components/FullScreenModal";
import { formatCurrency } from "@vaulzo/core/constants";
import { MONTHS_LONG, formatDay } from "@vaulzo/core/domain/expenses";
import { kicker, underline, PrimaryButton } from "./ui";

/** A group (a trip, an upgrade): its entries, a rename field, and the
 *  actions. Deleting the group keeps its entries as normal expenses. */
export default function GroupModal({ open, onClose, row, onAddEntry, onEditEntry, onDeleteEntry, onRename, onDeleteGroup, renaming = false }) {
  const [editingName, setEditingName] = useState(false);
  const [name, setName] = useState("");
  useEffect(() => {
    if (open) {
      setEditingName(false);
      setName(row?.group?.name || "");
    }
  }, [open, row?.group?.name]);
  if (!open || !row) return null;
  const { group, items, total } = row;
  const biggest = items.length > 1 ? [...items].sort((a, b) => b.amount - a.amount)[0] : null;
  const span = items.length > 1 ? `${formatDay(items[0].date, { year: false })} – ${formatDay(items[items.length - 1].date, { year: false })}` : items.length === 1 ? formatDay(items[0].date) : "no entries yet";
  const [y, m] = group.month.split("-");
  const submitName = (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    onRename(name.trim(), () => setEditingName(false));
  };

  return (
    <FullScreenModal open={open} onClose={onClose}>
      <div className="w-full max-w-[640px]">
        <div className={kicker}>Group</div>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="min-w-0">
            {editingName ? (
              <form onSubmit={submitName} className="flex items-center gap-2">
                <input value={name} onChange={(e) => setName(e.target.value)} autoFocus maxLength={80} className={`${underline} text-[24px] font-bold`} />
                <button type="submit" disabled={renaming} aria-label="Save name" className="flex h-9 w-9 flex-none items-center justify-center rounded-full bg-vaulzo-accent text-vaulzo-on-fill disabled:opacity-60">
                  <Check className="h-4 w-4" strokeWidth={2.2} />
                </button>
                <button type="button" onClick={() => setEditingName(false)} aria-label="Cancel" className="flex h-9 w-9 flex-none items-center justify-center rounded-full bg-vaulzo-row text-vaulzo-muted">
                  <X className="h-4 w-4" strokeWidth={2.2} />
                </button>
              </form>
            ) : (
              <h2 className="m-0 truncate text-[34px] font-bold tracking-[-0.03em] text-vaulzo-ink">{group.name}</h2>
            )}
            <div className="num mt-[10px] text-[28px] font-bold tracking-[-0.03em] text-vaulzo-ink">{formatCurrency(total)}</div>
            <div className="mt-2 text-sm text-vaulzo-faint">
              {items.length} {items.length === 1 ? "entry" : "entries"} · {span} · counted in {MONTHS_LONG[Number(m) - 1]} {y}
            </div>
            {biggest && (
              <div className="num mt-1 text-[13px] text-vaulzo-muted">
                Biggest: {biggest.name} · {formatCurrency(biggest.amount)}
              </div>
            )}
          </div>
          <PrimaryButton type="button" small onClick={onAddEntry}>
            <Plus className="mr-[6px] h-4 w-4" strokeWidth={2.2} />
            Add entry
          </PrimaryButton>
        </div>

        <div className="mt-7 overflow-hidden rounded-2xl border border-vaulzo-hairline bg-vaulzo-surface">
          {items.length === 0 && <div className="py-10 text-center text-sm text-vaulzo-subtle">Nothing in this group yet.</div>}
          {items.map((e, i) => (
            <div key={e.id} className={`flex items-center gap-3 px-4 py-3 text-sm ${i ? "border-t border-vaulzo-row" : ""}`}>
              <span className="num w-[52px] flex-none text-vaulzo-fainter">{formatDay(e.date, { year: false })}</span>
              <span className="min-w-0 flex-1 truncate text-vaulzo-ink">{e.name}</span>
              <span className="num font-semibold text-vaulzo-ink">{formatCurrency(e.amount)}</span>
              <span className="flex w-14 flex-none justify-end gap-1">
                <button type="button" onClick={() => onEditEntry(e)} aria-label="Edit" className="flex items-center p-[6px] text-vaulzo-fainter transition-colors hover:text-vaulzo-accent">
                  <Pencil className="h-[15px] w-[15px]" strokeWidth={1.7} />
                </button>
                <button type="button" onClick={() => onDeleteEntry(e)} aria-label="Delete" className="flex items-center p-[6px] text-vaulzo-bar transition-colors hover:text-vaulzo-danger">
                  <Trash2 className="h-[15px] w-[15px]" strokeWidth={1.7} />
                </button>
              </span>
            </div>
          ))}
        </div>

        <div className="mt-7 flex items-center gap-4">
          <button type="button" onClick={() => setEditingName(true)} className="text-sm font-semibold text-vaulzo-accent transition-colors hover:text-vaulzo-accent-hover">
            Rename group
          </button>
          <span className="flex-1" />
          <button type="button" onClick={onDeleteGroup} className="text-sm font-semibold text-vaulzo-danger transition-opacity hover:opacity-60">
            Delete group
          </button>
        </div>
      </div>
    </FullScreenModal>
  );
}
